import { AssignmentEntry, AssignmentMeta, AssignmentPin, assignmentPinToPin } from '@lessonup/assignments-shared';
import type { PinTheme } from '@lessonup/pins-shared';
import { PinComponent, TeacherPin } from '@lessonup/pins-shared';
import { find, isNull, orderBy, isEmpty } from 'lodash';
import {
  GetRawPinSliceOutput,
  InitializedPlayerStoreState,
  PinState,
  PlayerStoreState,
  Student,
} from './playerStore.types';

export function getPin(state: InitializedPlayerStoreState, pinId: string): TeacherPin {
  const assignmentPin = find(state.assignment.pins, (pin) => pin.id === pinId);
  if (!assignmentPin || !state.assignmentMeta) {
    throw new Error('Invalid pin id');
  }

  const pin = assignmentPinToTeacherPin(assignmentPin, state.assignmentMeta);
  const pinState = state.pinState[assignmentPin.id];

  if (pinState) {
    pin.pinComponents = pin.pinComponents.map((pinComponent) => {
      if (!assignmentPin.id) return pinComponent;
      if (pinState.componentState[pinComponent.id]) {
        return {
          ...pinComponent,
          layout: {
            ...(pinComponent.layout ?? {}),
            ...(pinState.componentState[pinComponent.id]?.layout ?? {}),
          },
          settings: {
            ...(pinComponent.settings ?? {}),
            ...(pinState.componentState[pinComponent.id]?.settings ?? {}),
          },
        };
      }
      return pinComponent;
    }) as PinComponent[];

    // Merge cloned components into pinComponents array
    const clonedComponents = pinState.dragAndDrop?.clonedComponents || {};
    if (!isEmpty(clonedComponents)) {
      const clonedComponentsArray = Object.values(clonedComponents).map((clone) => {
        const cloneState = pinState.componentState[clone.id];
        if (cloneState) {
          return {
            ...clone,
            layout: {
              ...(clone.layout ?? {}),
              ...(cloneState.layout ?? {}),
            },
            settings: {
              ...(clone.settings ?? {}),
              ...(cloneState.settings ?? {}),
            },
          } as PinComponent;
        }
        return clone as PinComponent;
      });

      pin.pinComponents = [...pin.pinComponents, ...clonedComponentsArray];
    }
  }

  return pin;
}

export function getRawPin(state: InitializedPlayerStoreState, pinId: string): TeacherPin {
  const assignmentPin = find(state.assignment.pins, (pin) => pin.id === pinId);
  if (!assignmentPin || !state.assignmentMeta) {
    throw new Error('Invalid pin id');
  }

  return assignmentPinToTeacherPin(assignmentPin, state.assignmentMeta);
}

export function currentPin(state: InitializedPlayerStoreState): TeacherPin | null {
  if (!state.currentPinId) {
    return null;
  }

  return getPin(state, state.currentPinId);
}

export function nextPin(state: InitializedPlayerStoreState): TeacherPin | null {
  if (!state.currentPinId) {
    return null;
  }
  const currentIndex = getCurrentIndex(state);
  const nextPinId = state.pinIdsOrder[currentIndex + 1];
  if (!nextPinId) {
    return null;
  }

  return getPin(state, nextPinId);
}

export function getRawPinSlices(
  state: InitializedPlayerStoreState,
  currentPinId: string,
  slices: number
): GetRawPinSliceOutput {
  const currentPinIndex = state.pinIdsOrder.findIndex((pinId) => pinId === currentPinId);
  const start = Math.max(0, currentPinIndex - slices);
  const end = currentPinIndex + 1 + slices;
  const previousPinIds = state.pinIdsOrder.slice(start, currentPinIndex);
  const nextPinIds = state.pinIdsOrder.slice(currentPinIndex + 1, end);
  const previousPins = previousPinIds.map((pinId) => getRawPin(state, pinId));
  const nextPins = nextPinIds.map((pinId) => getRawPin(state, pinId));

  return { previousPins, nextPins };
}

export function validPin(state: InitializedPlayerStoreState): TeacherPin {
  const pin = currentPin(state);
  if (!pin) {
    throw new Error('Pin not found');
  }
  return pin;
}

export function getCurrentIndex(state: InitializedPlayerStoreState) {
  return state.pinIdsOrder.findIndex((pinId) => pinId === state.currentPinId);
}

export function getComponentStateForCurrentPin(state: InitializedPlayerStoreState): PinState['componentState'] {
  return state.pinState[state.currentPinId]?.componentState;
}

export function assignmentPinToTeacherPin(assignmentPin: AssignmentPin, assignmentMeta: AssignmentMeta): TeacherPin {
  return assignmentPinToPin(assignmentPin, assignmentMeta);
}

export function getOrderedPinIds(state: PlayerStoreState) {
  if (isNull(state.assignment)) return [];

  return orderBy(state.assignment.pins, ['order']).map((p) => p.id);
}

export function studentFromAssignmentEntry(assignmentEntry: AssignmentEntry[]): Student[] {
  return assignmentEntry
    .map((assignmentEntry) => ({
      name: assignmentEntry.displayName,
      userId: assignmentEntry.userId,
    }))
    .filter((student): student is Student => !!student.name);
}

export async function fetchPinThemesMap(
  pins:
    | Record<string, { settings: { pinThemeId?: string | null } }>
    | Array<{ settings: { pinThemeId?: string | null } }>,
  getPinThemes: (ids: string[]) => Promise<PinTheme[]>
): Promise<Record<string, PinTheme>> {
  const pinArray = Array.isArray(pins) ? pins : Object.values(pins);
  const pinThemeIds = pinArray.map((pin) => pin.settings.pinThemeId).filter(Boolean) as string[];
  if (pinThemeIds.length === 0) return {};
  const themes = await getPinThemes(pinThemeIds);
  return Object.fromEntries(themes.map((t) => [t.id, t]));
}
